import { ComboboxOption } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import { pageKey } from '@dashboard/lib/queryKeys';
import useSearchInput from '@/dashboard/hooks/useSearchInput';
import Combobox from '../../Form/components/Combobox';
import { Label } from '@/dashboard/components/Form';

const PageFilterInput = ({
  placeholder = 'Filter by page',
  label,
  name,
  onChange,
  pageTypes = ['checkout'],
  productTypes = [],
  initialValue,
}) => {
  const [value, setValue] = useState(initialValue);

  const { getPages, getPage } = usePageQueries();
  const { sellerId } = useAuth();
  const [selectedPage, setSelectedPage] = useState();
  const { setSearchInput, search } = useSearchInput('');

  useEffect(() => {
    if (onChange) {
      onChange(value);
    }
  }, [onChange, value]);

  const { data: currentPageRedirect } = useQuery({
    queryKey: pageKey(value),
    queryFn: () => getPage(value),
    enabled: !!value,
  });

  useEffect(() => {
    if (!selectedPage) {
      setSelectedPage(currentPageRedirect);
      setSearchInput(currentPageRedirect?.name);
    }
  }, [selectedPage, currentPageRedirect, setSelectedPage, setSearchInput]);

  useEffect(() => {
    if (!selectedPage) return;

    if (selectedPage._id !== value) {
      setValue(selectedPage._id);
    }
  }, [selectedPage, value]);

  const { data, fetchNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['pages', search],
    queryFn: async ({ pageParam }) =>
      await getPages(sellerId, {
        status: ['published', 'draft'],
        type: pageTypes,
        productType: productTypes,
        limit: 10,
        skip: pageParam * 10,
        search: search === '' ? undefined : search,
        _id: initialValue === '' ? undefined : initialValue,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
  });

  const clear = () => {
    setValue(null);
    setSearchInput('');
    setSelectedPage(null);
  };

  return (
    <div>
      {label && <Label>{label}</Label>}

      <Combobox
        name={name}
        immediate
        value={selectedPage}
        onChange={value => {
          if (value == null) {
            clear();
            return;
          }

          setSearchInput(value.name);
          setSelectedPage(value);
        }}
        onClear={e => {
          e.preventDefault();
          clear();
        }}
        onChangeInput={e => setSearchInput(e.target.value)}
        placeholder={placeholder}
        hasItems={data?.pages?.[0]?.data?.length > 0}
        isError={isError}
        isLoading={isLoading}
        noItemsMsg="No pages found"
        fetchNextPage={fetchNextPage}
        showSearchIcon={true}
      >
        {data?.pages?.map(data =>
          data?.data.map(page => (
            <ComboboxOption
              key={page._id}
              value={page}
              className={({ focus }) => `cursor-pointer relative ${focus ? 'bg-gray-100' : ''}`}
            >
              <div
                key={page._id}
                selected={page._id === selectedPage?._id}
                type="button"
                className={clsx(
                  'flex justify-start items-center w-full text-left p-2 hover:bg-gray-100 transition-colors border-b border-gray-100 !rounded-b-none',
                  {
                    '!bg-gray-50': page._id === selectedPage?._id,
                  },
                )}
              >
                <div>
                  <div className="font-medium">{page.name}</div>
                  {page.type === 'checkout' && (
                    <div className="mt-1 text-gray-700">
                      <span>{getProductPriceLabel(page.product)}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-x-4 ml-auto items-center">
                  {page._id === selectedPage?._id && (
                    <div>
                      <CheckIcon className="w-5 h-5 text-gray-500" />
                    </div>
                  )}
                </div>
              </div>
            </ComboboxOption>
          )),
        )}
      </Combobox>
    </div>
  );
};

export default PageFilterInput;
